import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Plus, Users, Sparkles, Brain, Palette, Crown, Wand2, LogIn, UserPlus, Star, Zap, Rocket, Trophy, Wand, Gem, Compass, Heart, Lightbulb, Target, Paintbrush, Camera, Music, BookOpen, Scissors, Brush, PenTool, Image, Layers, Eye, Gamepad2, Play, Home, Settings, Pen } from 'lucide-react'
import { databaseService } from '@/services/database'
import { authService, AuthUser } from '@/services/auth'
import { trackEvent } from '@/services/analytics'
import { UserLimits } from '@/types'
import UserDropdown from '@/components/UserDropdown'
import { useTranslation } from 'react-i18next'
import LanguageSwitcher from '@/components/LanguageSwitcher'

export default function HomePage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [isCreating, setIsCreating] = useState(false)
  const [isJoining, setIsJoining] = useState(false)
  const [roomName, setRoomName] = useState('')
  const [joinRoomId, setJoinRoomId] = useState('')
  const [error, setError] = useState('')
  const [roomNameError, setRoomNameError] = useState('')
  const [joinRoomError, setJoinRoomError] = useState('')
  const [promptTimeLimit, setPromptTimeLimit] = useState(300)
  const [votingTimeLimit, setVotingTimeLimit] = useState(180)
  const [maxRounds, setMaxRounds] = useState(3)
  const [gameMode, setGameMode] = useState<'word_to_prompt' | 'user_word_to_prompt'>('word_to_prompt')
  const [isInitializing, setIsInitializing] = useState(true)
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null)
  const [userLimits, setUserLimits] = useState<UserLimits | null>(null)
  const [welcome, setWelcome] = useState<string | null>(null)
  const [authMenuOpen, setAuthMenuOpen] = useState(false)
  
  // Slot makinesi animasyonu için state'ler
  const [currentIcons, setCurrentIcons] = useState([0, 1, 2])
  
  // İkon listesi - farklı kategorilerden ikonlar
  const iconSets = [
    [Sparkles, Crown, Star, Zap, Rocket, Trophy, Wand, Gem, Compass], // Yaratıcılık ve büyü
    [Palette, Heart, Lightbulb, Brain, Target, Paintbrush, Camera, Music, BookOpen, Scissors, Brush, PenTool, Image, Layers, Eye], // Sanat ve düşünce
    [Wand2, Gamepad2, Users, Play, Sparkles], // Oyun ve sosyal
  ]

  useEffect(() => {
    // Initialize authentication
    const initAuth = async () => {
      try {
        const user = await authService.getCurrentUser()
        setCurrentUser(user)
        
        // Load user limits if user exists (secure init first)
        if (user) {
          const provider = user.app_metadata?.provider || 'email'
          const providerData = {
            providerId: user.app_metadata?.provider_id || user.user_metadata?.sub,
            email: user.email
          }
          let limits
          try {
            limits = await authService.initializeUserLimitsSecure(provider, providerData)
          } catch (securityError) {
            console.warn('Security check failed on init, falling back:', securityError)
            limits = await authService.getUserLimits()
          }
          setUserLimits(limits)
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
      } finally {
        setIsInitializing(false)
      }
    }

    initAuth()

    // Listen for auth state changes
    const { data: { subscription } } = authService.onAuthStateChange(async (user) => {
      setCurrentUser(user)
      if (user) {
        try {
          // Initialize secure user limits with provider information
          const provider = user.app_metadata?.provider || 'email'
          const providerData = {
            providerId: user.app_metadata?.provider_id || user.user_metadata?.sub,
            email: user.email
          }
          
          // Try to initialize secure limits first, fallback to regular limits
          let limits
          try {
            limits = await authService.initializeUserLimitsSecure(provider, providerData)
          } catch (securityError) {
            console.warn('Security check failed, falling back to regular limits:', securityError)
            limits = await authService.getUserLimits()
          }
          
          setUserLimits(limits)
        } catch (error) {
          console.error('Error loading user limits:', error)
          setUserLimits(null)
        }
      } else {
        setUserLimits(null)
      }
    })

    return () => {
      subscription?.unsubscribe()
    }
  }, [])

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const menu = document.getElementById('authMenu')
      const btn = document.getElementById('authMenuBtn')
      if (menu && !menu.contains(e.target as Node) && btn && !btn.contains(e.target as Node)) {
        setAuthMenuOpen(false)
      }
    }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [])

  useEffect(() => {
    try {
      const msg = sessionStorage.getItem('welcome_message')
      if (msg) {
        setWelcome(msg)
        sessionStorage.removeItem('welcome_message')
      }
    } catch {}
  }, [])

  // Slot makinesi animasyonu
  useEffect(() => {
    let currentIndex = 0
    const totalIcons = currentIcons.length
    const animationDuration = 2000 // 2 saniye
    const delayBetweenIcons = 1500 // İkonlar arası 1.5 saniye bekleme
    
    const changeNextIcon = () => {
      setCurrentIcons(prev => {
        const newIcons = [...prev]
        const availableIcons = iconSets[currentIndex % iconSets.length]
        newIcons[currentIndex] = Math.floor(Math.random() * availableIcons.length)
        return newIcons
      })
      
      currentIndex = (currentIndex + 1) % totalIcons
    }
    
    // İlk değişim
    const initialTimeout = setTimeout(changeNextIcon, 3000)
    
    // Sonraki değişimler için interval
    const interval = setInterval(changeNextIcon, animationDuration + delayBetweenIcons)
    
    return () => {
      clearTimeout(initialTimeout)
      clearInterval(interval)
    }
  }, [])

  const handleSignOut = async () => {
    try {
      await authService.signOut()
      setCurrentUser(null)
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  const handleCreateRoom = async () => {
    if (!roomName.trim()) {
      setRoomNameError(t('home.errors.roomNameRequired'))
      setJoinRoomError('')
      setError('')
      return
    }

    // Require authenticated user and use username/email prefix
    if (!currentUser) {
      navigate('/login')
      return
    }
    const displayName = currentUser.username || currentUser.email?.split('@')[0] || 'User'

    setRoomNameError('')
    setJoinRoomError('')
    setError('')
    setIsCreating(true)

    try {
      const { room, player } = await databaseService.createRoom(
        roomName.trim(),
        displayName,
        {
          promptTimeLimit,
          votingTimeLimit,
          maxRounds,
          maxPlayers: undefined,
        },
        gameMode
      )
      
      // Analytics: oda olusturma basarili
      trackEvent({
        event: 'scraible_room_create',
        properties: {
          room_id: room.id,
          room_code: room.code,
          game_mode: gameMode,
          max_rounds: maxRounds,
          prompt_time_sec: promptTimeLimit,
          voting_time_sec: votingTimeLimit,
          is_host: player.isHost,
        },
      })

      // Store player info in localStorage (for persistence across pages)
      localStorage.setItem('currentPlayer', JSON.stringify(player))
      localStorage.setItem('playerName', displayName)
      
      navigate(`/lobby/${room.id}`)
    } catch (error: any) {
      setRoomNameError(error.message || t('home.errors.createRoom'))
    } finally {
      setIsCreating(false)
    }
  }

  const handleJoinRoom = async () => {
    if (!joinRoomId.trim()) {
      setJoinRoomError(t('home.errors.joinCodeRequired'))
      setRoomNameError('')
      setError('')
      return
    }

    // Require authenticated user and use username/email prefix
    if (!currentUser) {
      navigate('/login')
      return
    }
    const displayName = currentUser.username || currentUser.email?.split('@')[0] || 'User'

    setJoinRoomError('')
    setRoomNameError('')
    setError('')
    setIsJoining(true)

    try {
      const { room, player } = await databaseService.joinRoom(
        joinRoomId.trim(),
        displayName
      )
      
      // Analytics: odaya katilim basarili
      trackEvent({
        event: 'scraible_room_join',
        properties: {
          room_id: room.id,
          room_code: room.code,
          method: 'code',
          is_host: player.isHost,
        },
      })

      // Store player info in localStorage (for persistence across pages)
      localStorage.setItem('currentPlayer', JSON.stringify(player))
      localStorage.setItem('playerName', displayName)
      
      navigate(`/lobby/${room.id}`)
    } catch (error: any) {
      setJoinRoomError(error.message || t('home.errors.joinRoom'))
    } finally {
      setIsJoining(false)
    }
  }

  const features = [
    {
      icon: <Brain className="w-10 h-10" />,
      title: t('home.features.creative.title'),
      description: t('home.features.creative.desc'),
      color: 'from-violet-500 to-purple-500'
    },
    {
      icon: <Wand2 className="w-10 h-10" />,
      title: t('home.features.aiMagic.title'),
      description: t('home.features.aiMagic.desc'),
      color: 'from-pink-500 to-rose-500'
    },
    {
      icon: <Crown className="w-10 h-10" />,
      title: t('home.features.contest.title'),
      description: t('home.features.contest.desc'),
      color: 'from-amber-500 to-orange-500'
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: t('home.features.social.title'),
      description: t('home.features.social.desc'),
      color: 'from-emerald-500 to-teal-500'
    }
  ]

  if (isInitializing) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-6">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-white/20 border-t-violet-400 rounded-full animate-spin mx-auto"></div>
            <div className="absolute inset-0 w-16 h-16 border-2 border-purple-400/30 rounded-full animate-pulse mx-auto"></div>
          </div>
          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-white">{t('home.loadingTitle')}</h3>
            <p className="text-white/70">{t('home.loadingSubtitle')}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {welcome && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mx-4 p-3 rounded-xl bg-emerald-500/10 border border-emerald-400/30 text-emerald-300 text-center"
        >
          {welcome}
        </motion.div>
      )}
      
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex justify-between items-center p-4 sm:p-6"
      >
        <div className="flex items-center space-x-0">
          <div className="relative w-12 h-12 flex items-center justify-center">
            <img 
              src="/scraiblelogo2.png" 
              alt="Scraible Logo" 
              className="max-w-full max-h-full object-contain rounded-lg"
              style={{ aspectRatio: '1/1' }}
            />
            <div className="absolute inset-0 w-12 h-12 bg-violet-400/20 rounded-lg blur-lg animate-pulse"></div>
          </div>
          <h1 className="text-xl font-bold text-gradient pr-2">{t('home.brand')}</h1>
        </div>

        <div className="flex items-center space-x-3">
          <LanguageSwitcher variant="menu" />
          {currentUser ? (
            <UserDropdown 
              user={currentUser}
              userLimits={userLimits}
              onSignOut={handleSignOut}
            />
          ) : (
            <div className="relative">
              <button
                id="authMenuBtn"
                onClick={() => setAuthMenuOpen(!authMenuOpen)}
                className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors"
              >
                <LogIn className="w-4 h-4" />
                <span className="text-sm">{t('home.login')}</span>
              </button>
              {authMenuOpen && (
                <div
                  id="authMenu"
                  className="absolute right-0 mt-2 w-56 bg-slate-800/95 backdrop-blur-md border border-white/20 rounded-xl shadow-xl p-2 z-50 safe-area-right"
                >
                  <Link
                    to="/login"
                    onClick={() => setAuthMenuOpen(false)}
                    className="flex items-center space-x-2 w-full px-3 py-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <LogIn className="w-4 h-4" />
                    <span className="text-sm">{t('home.login')}</span>
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setAuthMenuOpen(false)}
                    className="flex items-center space-x-2 w-full px-3 py-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <UserPlus className="w-4 h-4" />
                    <span className="text-sm">{t('home.register')}</span>
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.header>

      {/* Hero Section with Slot Machine Animation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center space-y-6 py-8 px-4"
      >
        <div className="relative">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex items-center justify-center space-x-2 sm:space-x-4 mb-6"
          >
            {currentIcons.map((iconIndex, index) => {
              const IconComponent = iconSets[index % iconSets.length][iconIndex]
              const colors = ['text-violet-500', 'text-pink-400', 'text-purple-400']
              const sizes = ['w-8 h-8 sm:w-12 sm:h-12', 'w-8 h-8 sm:w-12 sm:h-12', 'w-8 h-8 sm:w-12 sm:h-12']
              
              return (
                <div key={index} className="relative overflow-hidden flex items-center justify-center" style={{ 
                  height: '60px',
                  maskImage: 'linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%)',
                  WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%)'
                }}>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={`${index}-${iconIndex}`}
                      initial={{ y: -120, rotateX: -90 }}
                      animate={{ y: 0, rotateX: 0 }}
                      exit={{ y: 120, rotateX: 90 }}
                      transition={{ 
                        duration: 2, 
                        ease: [0.25, 0.46, 0.45, 0.94], // Custom easing for slot machine feel
                        type: "tween"
                      }}
                      className="relative flex items-center justify-center"
                    >
                      <IconComponent className={`${sizes[index]} ${colors[index]}`} />
                    </motion.div>
                  </AnimatePresence>
                </div>
              )
            })}
          </motion.div>
          
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gradient mb-4">
            {t('home.heroTitleTop')}
            <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent animate-pulse">ai</span>
            <span className="text-gradient-gold">{t('home.heroTitleBottom')}</span>
          </h1>
          
          <p className="text-sm sm:text-base text-white/70 max-w-2xl mx-auto">
            {t('home.heroTagline1')} <span className="text-gradient font-semibold">{t('home.heroTagline2')}</span>! 
            {t('home.heroTagline3')} 
            <span className="text-gradient-emerald font-semibold"> {t('home.heroTagline4')}</span> {t('home.heroTagline5')}
          </p>
        </div>
      </motion.div>

      {/* Main Action Cards - Moved to top */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 px-4"
      >
        {/* Create Room Card */}
        <motion.div 
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="card space-y-6 border-2 border-violet-400/30 bg-gradient-to-br from-violet-500/10 to-purple-500/10"
        >
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg neon-glow">
              <Plus className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">{t('home.createRoom.title')}</h2>
              <p className="text-white/70">{t('home.createRoom.subtitle')}</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <input
                type="text"
                placeholder={t('home.createRoom.placeholder')}
                value={roomName}
                onChange={(e) => {
                  setRoomName(e.target.value)
                  if (roomNameError) setRoomNameError('')
                }}
                onFocus={() => {
                  if (roomNameError) setRoomNameError('')
                }}
                className={`input-field text-lg ${roomNameError ? 'border-red-400 bg-red-500/10 focus:border-red-400 focus:ring-red-400/20' : ''}`}
                maxLength={50}
              />
              {roomNameError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-2 flex items-center space-x-2 text-red-400 text-sm"
                >
                  <span className="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">!</span>
                  </span>
                  <span>{roomNameError}</span>
                </motion.div>
              )}
            </div>

            {/* Game Mode Selection */}
            <div>
              <label className="text-sm font-medium text-white/80 mb-2 block">{t('home.createRoom.mode')}</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setGameMode('word_to_prompt')}
                  className={`p-3 rounded-lg border-2 text-sm font-semibold transition-all ${
                    gameMode === 'word_to_prompt'
                      ? 'bg-gradient-to-r from-violet-500/30 to-purple-500/30 border-violet-400 text-white shadow-lg'
                      : 'bg-white/5 border-white/20 text-white/70 hover:bg-white/10 hover:border-white/30'
                  }`}
                >
                  {t('home.createRoom.classic.title')}
                </button>
                <button
                  type="button"
                  onClick={() => setGameMode('user_word_to_prompt')}
                  className={`p-3 rounded-lg border-2 text-sm font-semibold transition-all ${
                    gameMode === 'user_word_to_prompt'
                      ? 'bg-gradient-to-r from-violet-500/30 to-purple-500/30 border-violet-400 text-white shadow-lg'
                      : 'bg-white/5 border-white/20 text-white/70 hover:bg-white/10 hover:border-white/30'
                  }`}
                >
                  {t('home.createRoom.userWord.title')}
                </button>
              </div>
            </div>
            
            {/* Game Settings */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-white/80 mb-2 block">{t('home.createRoom.promptTime')}</label>
                <input
                  type="number"
                  min={30}
                  max={900}
                  value={promptTimeLimit}
                  onChange={(e) => setPromptTimeLimit(Number(e.target.value))}
                  className="input-field"
                />
                <div className="text-xs text-white/50 mt-1">{Math.floor(promptTimeLimit / 60)} {t('common.minutesShort')}</div>
              </div>
              <div>
                <label className="text-sm font-medium text-white/80 mb-2 block">{t('home.createRoom.votingTime')}</label>
                <input
                  type="number"
                  min={30}
                  max={600}
                  value={votingTimeLimit}
                  onChange={(e) => setVotingTimeLimit(Number(e.target.value))}
                  className="input-field"
                />
                <div className="text-xs text-white/50 mt-1">{Math.floor(votingTimeLimit / 60)} {t('common.minutesShort')}</div>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-white/80 mb-2 block">{t('home.createRoom.rounds')}</label>
              <select
                value={maxRounds}
                onChange={(e) => setMaxRounds(Number(e.target.value))}
                className="select-field"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((round) => (
                  <option key={round} value={round}>
                    {round} {round === 1 ? t('common.round') : t('common.rounds')}
                  </option>
                ))}
              </select>
            </div>
            
            <motion.button
              onClick={handleCreateRoom}
              disabled={isCreating}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 py-4 text-lg font-semibold"
            >
              {isCreating ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>{t('home.createRoom.creating')}</span>
                </>
              ) : (
                <>
                  <Plus className="w-6 h-6" />
                  <span>{t('home.createRoom.create')}</span>
                </>
              )}
            </motion.button>
          </div>
        </motion.div>

        {/* Join Room Card */}
        <motion.div 
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="card space-y-6 border-2 border-emerald-400/30 bg-gradient-to-br from-emerald-500/10 to-teal-500/10"
        >
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg neon-glow">
              <Users className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">{t('home.joinRoom.title')}</h2>
              <p className="text-white/70">{t('home.joinRoom.subtitle')}</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <input
                type="text"
                placeholder={t('home.joinRoom.placeholder')}
                value={joinRoomId}
                onChange={(e) => {
                  setJoinRoomId(e.target.value)
                  if (joinRoomError) setJoinRoomError('')
                }}
                onFocus={() => {
                  if (joinRoomError) setJoinRoomError('')
                }}
                className={`input-field text-center text-xl font-mono tracking-widest ${joinRoomError ? 'border-red-400 bg-red-500/10 focus:border-red-400 focus:ring-red-400/20' : ''}`}
                maxLength={6}
                pattern="[0-9]{6}"
              />
              {joinRoomError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-2 flex items-center space-x-2 text-red-400 text-sm"
                >
                  <span className="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">!</span>
                  </span>
                  <span>{joinRoomError}</span>
                </motion.div>
              )}
            </div>

            <motion.button
              onClick={handleJoinRoom}
              disabled={isJoining}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="btn-success w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 py-4 text-lg font-semibold"
            >
              {isJoining ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>{t('home.joinRoom.joining')}</span>
                </>
              ) : (
                <>
                  <Users className="w-6 h-6" />
                  <span>{t('home.joinRoom.join')}</span>
                </>
              )}
            </motion.button>
          </div>
        </motion.div>
      </motion.div>

      {/* General Error Message - Only show if no specific field errors */}
      {error && !roomNameError && !joinRoomError && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="card-gradient border-red-400/50 bg-red-500/10"
        >
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-bold">!</span>
            </div>
            <p className="text-red-400 font-medium">{error}</p>
          </div>
        </motion.div>
      )}

      {/* Quick Features */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.8 }}
        className="grid grid-cols-2 md:grid-cols-4 gap-4 px-4"
      >
        {features.map((feature, index) => (
          <motion.div
            key={feature.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 1 + index * 0.1 }}
            className="text-center space-y-3 p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all duration-300"
          >
            <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${feature.color} shadow-lg`}>
              <div className="text-white">
                {feature.icon}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">
                {feature.title}
              </h3>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Compact How to Play */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.2 }}
        className="card-gradient border-cyan-400/30"
      >
        <div className="text-center mb-6 px-4">
          <h2 className="text-xl font-bold text-gradient mb-2">
            {t('home.howToPlay.title')}
          </h2>
          <p className="text-white/70 text-sm">{t('home.howToPlay.subtitle')}</p>
        </div>
        
        {/* Enhanced Game Steps */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 px-4">
          {[
            { step: 1, icon: <Home className="w-5 h-5" />, title: t('home.howToPlay.steps.0.title'), desc: t('home.howToPlay.steps.0.desc') },
            { step: 2, icon: <Settings className="w-5 h-5" />, title: t('home.howToPlay.steps.1.title'), desc: t('home.howToPlay.steps.1.desc') },
            { step: 3, icon: <Pen className="w-5 h-5" />, title: t('home.howToPlay.steps.2.title'), desc: t('home.howToPlay.steps.2.desc') },
            { step: 4, icon: <Palette className="w-5 h-5" />, title: t('home.howToPlay.steps.3.title'), desc: t('home.howToPlay.steps.3.desc') },
            { step: 5, icon: <Star className="w-5 h-5" />, title: t('home.howToPlay.steps.4.title'), desc: t('home.howToPlay.steps.4.desc') },
            { step: 6, icon: <Trophy className="w-5 h-5" />, title: t('home.howToPlay.steps.5.title'), desc: t('home.howToPlay.steps.5.desc') }
          ].map((item, index) => (
            <motion.div
              key={item.step}
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.4, delay: 1.4 + index * 0.1, type: "spring", stiffness: 100 }}
              whileHover={{ scale: 1.02, y: -2 }}
              className="group relative bg-white/5 backdrop-blur-sm p-4 rounded-2xl border border-white/10 hover:border-white/20 transition-all duration-300 hover:bg-white/10"
            >
              {/* Step Number Badge */}
              <div className="absolute -top-2 -left-2 w-8 h-8 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-lg">
                {item.step}
              </div>
              
              {/* Icon */}
              <div className="text-violet-400 flex justify-center mb-3 mt-2 group-hover:text-violet-300 transition-colors">
                {item.icon}
              </div>
              
              {/* Content */}
              <div className="text-center">
                <h4 className="font-semibold text-white text-sm mb-2 group-hover:text-white transition-colors">
                  {item.title}
                </h4>
                <p className="text-white/60 text-xs leading-relaxed">
                  {item.desc}
                </p>
              </div>
              
              {/* Hover Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-violet-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  )
}
